import { z } from 'zod';
/**
 * GIFs from GIPHY (docs/adr/0019-gifs.md). The API searches GIPHY with the workspace's content rating and
 * keeps the key on the server; apps send only a GIPHY id, and the server looks the GIF up again before
 * storing it on the message. GIF bytes are loaded from GIPHY's media hosts, as GIPHY's terms require.
 */
export const GIPHY_ID = /^[A-Za-z0-9]{1,64}$/;
export const Gif = z.object({
    id: z.string().regex(GIPHY_ID),
    title: z.string().max(200),
    /** The animation to show in a message (https://media*.giphy.com only). */
    url: z.url(),
    /** A smaller, lighter rendition for picker grids. */
    previewUrl: z.url(),
    /**
     * A still first frame of the preview, when GIPHY has one: shown instead of the animation to people who ask
     * for reduced motion (https://media*.giphy.com only).
     */
    stillUrl: z.url().optional(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const GifSearchQuery = z.object({
    /** Empty shows what is trending. */
    q: z.string().trim().max(50).optional(),
    offset: z.coerce.number().int().min(0).max(4999).optional(),
});
export const GifSearchResults = z.object({
    items: z.array(Gif),
    /** Null when there are no more results. */
    nextOffset: z.number().int().nullable(),
});
/** Hosts GIPHY serves animations from. Anything else is refused. */
export const GIPHY_MEDIA_HOST = /^media[0-9]?\.giphy\.com$/;
/** The suggestion chips under GIF search, the same on the phone and the web (after "Trending"). */
export const GIF_SUGGESTIONS = ['Thank you', 'Celebrate', 'Yes', 'Nice one', 'Facepalm', 'High five', 'Coffee', 'Mind blown'];
/** Height of a GIF tile in a column: aspect ratio kept, very tall or very wide GIFs clamped. */
export const tileHeight = (g, columnWidth) => Math.round(columnWidth / Math.max(0.4, Math.min(2.5, g.width / g.height)));
/** Splits GIFs into two columns, each next GIF going to the shorter column (a simple masonry). */
export function masonry(items, columnWidth, gap = 6) {
    const cols = [[], []];
    const heights = [0, 0];
    for (const g of items) {
        const i = heights[0] <= heights[1] ? 0 : 1;
        cols[i].push(g);
        heights[i] += tileHeight(g, columnWidth) + gap;
    }
    return cols;
}
//# sourceMappingURL=gifs.js.map